import {
  IconButton,
  Input,
  Menu,
  MenuItem,
  MenuTrigger,
} from '@affine/component';
import { CloseIcon } from '@blocksuite/icons/rc';
import { type FC, type ReactNode } from 'react';

import * as styles from './settings-navigation.css';

export interface SettingsTabItem {
  id: string;
  label: ReactNode;
  testId?: string;
  disabled?: boolean;
}

export interface SettingsViewModeItem {
  id: string;
  label: ReactNode;
  icon?: ReactNode;
}

export const SettingsTabs: FC<{
  tabs: SettingsTabItem[];
  activeTab: string;
  onChange: (tabId: string) => void;
  ariaLabel?: string;
}> = ({ tabs, activeTab, onChange, ariaLabel }) => {
  if (tabs.length === 0) {
    return null;
  }

  return (
    <div className={styles.tabBar} role="tablist" aria-label={ariaLabel}>
      {tabs.map(tab => (
        <button
          key={tab.id}
          type="button"
          className={styles.tabButton}
          data-active={activeTab === tab.id}
          data-testid={tab.testId}
          disabled={tab.disabled}
          onClick={() => onChange(tab.id)}
        >
          {tab.label}
        </button>
      ))}
    </div>
  );
};

export const SettingsListControls: FC<{
  searchValue: string;
  onSearchChange: (value: string) => void;
  searchPlaceholder: string;
  action?: ReactNode;
}> = ({ searchValue, onSearchChange, searchPlaceholder, action }) => {
  return (
    <div className={styles.searchSection}>
      <Input
        className={styles.searchInput}
        value={searchValue}
        onChange={onSearchChange}
        placeholder={searchPlaceholder}
        endFix={
          searchValue ? (
            <IconButton size={20} onClick={() => onSearchChange('')}>
              <CloseIcon />
            </IconButton>
          ) : undefined
        }
      />
      {action}
    </div>
  );
};

export const SettingsViewModeMenu: FC<{
  activeId: string;
  label: ReactNode;
  items: SettingsViewModeItem[];
  onChange: (id: string) => void;
}> = ({ activeId, label, items, onChange }) => {
  return (
    <Menu
      items={
        <>
          {items.map(item => (
            <MenuItem
              key={item.id}
              selected={activeId === item.id}
              prefixIcon={item.icon}
              onSelect={() => onChange(item.id)}
            >
              {item.label}
            </MenuItem>
          ))}
        </>
      }
      contentOptions={{ align: 'end' }}
    >
      <MenuTrigger
        className={styles.roleMenuTrigger}
        contentStyle={{ width: 'auto', flex: 'none' }}
      >
        {label}
      </MenuTrigger>
    </Menu>
  );
};

export const SettingsPagination: FC<{
  page: number;
  pageCount: number;
  pageSize: number;
  totalItems: number;
  onPageChange: (page: number) => void;
  previousLabel: string;
  nextLabel: string;
}> = ({
  page,
  pageCount,
  pageSize,
  totalItems,
  onPageChange,
  previousLabel,
  nextLabel,
}) => {
  if (totalItems === 0 || pageCount <= 1) {
    return null;
  }

  const start = (page - 1) * pageSize + 1;
  const end = Math.min(page * pageSize, totalItems);

  return (
    <div className={styles.paginationBar}>
      <div className={styles.paginationMeta}>
        {`${start}-${end} / ${totalItems}`}
      </div>
      <div className={styles.paginationActions}>
        <button
          type="button"
          className={styles.paginationButton}
          onClick={() => onPageChange(page - 1)}
          disabled={page === 1}
        >
          {previousLabel}
        </button>
        <button
          type="button"
          className={styles.paginationButton}
          onClick={() => onPageChange(page + 1)}
          disabled={page === pageCount}
        >
          {nextLabel}
        </button>
      </div>
    </div>
  );
};
